import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import Articles from '../../articlesData';
import ArticleWrapper from '../../components/ArticleWrapper';
import { AudioPlayer } from '../../components/AudioProvider';
import { ExploreMoreLinks, ArticleTitleSection } from '../../components/ArticleComponents';


const DK1_TKL = () => {
  const navigate = useNavigate();
  const article = Articles.find((a) => a.id === 'dk1_tkl');
  useEffect(() => {
    if (!article.visible) {
      navigate('/in-progress');
    }
  }, [navigate, article.visible]);

  return (
    <ArticleWrapper articleId="dk1_tkl">
      <div className="min-h-screen bg-[#111112] flex flex-col w-full">
        <ArticleTitleSection
          image='/pictures/articles/dk1_tkl/banner.JPG'
          title='DK1-TKL Review' 
          date={article.date}
          status={article.status}
        />
        {/* Content section with side-by-side layout */}
          <div className="w-full flex flex-col gap-16 px-4 bp1080:px-8 mb-16">

            <div className="w-full flex flex-col bp1080:flex-row-reverse items-center gap-8">
              <img
                src="/pictures/articles/dk1_tkl/pic1.JPG"
                alt="Pavlov Design"
                className="w-full bp1080:w-[75vw] h-[60vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center bp1080:text-left bp1080:ml-[1vw] bp1080:mr-[3vw]">
                <h2 className="text-3xl font-bold mb-4">Disclaimer</h2>
                <p>
                  I am affiliated with Unikeys, but this keyboard was bought with my own money. This review reflects my honest opinions and experiences with the product.
                </p><br />
                <h2 className="text-3xl font-bold mb-4">Overview</h2>
                <p>
                  The <b>GDK Lab DK1-TKL</b> is a feature-rich TKL that aims to deliver flexibility in <b>sound</b>, <b>feel</b>, and <b>design</b> at a highly competitive price point. After spending about two weeks with the DK1-TKL Metal Flow edition, I believe this board stands out as one of the strongest TKL offerings currently available. Between its <b>six mounting styles</b>, premium materials and finishes, and thoughtful design accents, the DK1-TKL manages to strike a balance between enthusiast-level customization and accessible pricing.
                </p><br />
              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row items-center gap-8">
              <img
                src="/pictures/articles/dk1_tkl/pic2.JPG"
                alt="Pavlov Case"
                className="w-full md:w-[45vw] h-[50-vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:ml-[2vw] md:text-left md:max-w-[45vw]">
                <h2 className="text-3xl font-bold mb-4">Design</h2>
                <p className="mb-4">Visually, the DK1-TKL is among the best-looking TKLs I’ve handled. The <b>Marblano/Metal Flow</b> finish gives the board a striking, one-of-a-kind appearance that catches light beautifully. Several design details elevate the board above more standard offerings:
                  <br /><br />
                  <b>Front Accent:</b> Available in PVD or concrete, this accent features a controllable LED notch that doubles as a caps lock indicator—subtle but thoughtful.
                  <br /><br />
                  <b>Bottom Plate:</b> Offered in brass or copper, with cleanly executed text that feels premium.
                  <br /><br />
                  <b>Finish Options:</b> The Marblano Metal Flow variant, in particular, sets this board apart aesthetically from other TKLs like the Cycle8, +84, or Neo80 Cu, which trend toward simpler finishes.
                  <br /><br />
                  The Metal Flow option has quickly made the DK1-TKL one of my favorite-looking boards in my collection. It strikes a balance between being unique and being refined, rather than flashy for the sake of it.
                </p>
              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row-reverse items-center gap-8">
              <img
                src="/pictures/articles/dk1_tkl/pic3.JPG"
                alt="Pavlov Typing"
                className="w-full md:w-[45vw] h-[110vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:text-left">
                <h2 className="text-3xl font-bold mb-4">Sound</h2>
                <p>
                  One of the DK1-TKL’s defining traits is its wide sound range, made possible by 3 distinct mounting styles each with 2 hardness levels: Dumbbell, Gasket, and Gasket Sock. Each configuration creates a unique typing signature, giving users freedom to tailor the board to their preferences.
                  <br /><br />
                  <b>Softer Mounts(gray)</b> produced a high-pitched, clacky profile that reminded me of boards like the Mode Envoy or Encore. It wasn’t overwhelming but leaned toward a brighter, sharper sound that some users will love.
                  <br /><br />
                  <b>Harder Mounts(black)</b> delivered a deeper, more resonant tone, which created a satisfying thockier sound signature. I particularly enjoyed the hard sock mount, which closely emulated a traditional top mount feel both in sound and feel.
                  <br /><br />
                  The DK1 also ships with case foam, plate foam, and PE foam, allowing further tuning. For example, adding case foam immediately softened the slight resonance I noticed in the modifiers during foamless setups. This resonance was not a dealbreaker and could be easily addressed by using a deskmat or KeebMat.
                </p><br /><br />
                <h2 className="text-3xl font-bold mb-4">Feel</h2>
                <p>
                  In terms of typing feel, the DK1-TKL lands somewhere in the middle of the firmness spectrum, offering comfort without extreme flex.
                  <br /><br />
                  <b>Softer mounts:</b> Delivered a cushioned experience that allowed me to type for hours without fatigue. The flex wasn’t excessive, which I liked. It never felt “mushy.”
                  <br /><br />
                  <b>Harder mounts:</b> Significantly stiffened up the typing feel. On the sock mount in particular, the feedback felt reminiscent of a top mount, which added variety to the board’s character.
                  <br /><br />
                  Overall, the board is adaptable depending on your build choices. Personally, I enjoyed the softer options for extended use, but I could see myself switching to the stiffer styles when I want something more precise and tactile.
                </p><br />
              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row items-center gap-8">
              <img
                src="/pictures/articles/dk1_tkl/pic4.JPG"
                alt="Pavlov Sound Test"
                className="w-full md:w-[45vw] h-[110vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:text-left md:ml-[2vw] md:max-w-[45vw]">
                <h2 className="text-3xl font-bold mb-4">Mounting Styles</h2>
                <p className="mb-4">Here are some sound tests of each mounting style. 
                <br /><br />  
                All use the same build: Alu Plate, Cherry Mx2A Blacks switches, GMK Keycaps, and no foams.
                </p>
                <AudioPlayer src="/audio/dk1_tkl/dk1_tkl_mx2a_softdumbbell.mp3" className="w-full mt-5" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>Soft Dumbbell</p>
                <AudioPlayer src="/audio/dk1_tkl/dk1_tkl_mx2a_softgasket.mp3" className="w-full mt-2" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>Soft Gasket</p>
                <AudioPlayer src="/audio/dk1_tkl/dk1_tkl_mx2a_softsock.mp3" className="w-full mt-2" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>Soft Sock</p>
                <AudioPlayer src="/audio/dk1_tkl/dk1_tkl_mx2a_harddumbbell.mp3" className="w-full mt-2" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>Hard Dumbbell</p>
                <AudioPlayer src="/audio/dk1_tkl/dk1_tkl_mx2a_hardgasket.mp3" className="w-full mt-2" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>Hard Gasket</p>
                <AudioPlayer src="/audio/dk1_tkl/dk1_tkl_mx2a_hardsock.mp3" className="w-full mt-2" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>Hard Sock</p>
                <h2 className="text-3xl font-bold mb-4">Foam</h2>
                <p className="mb-4">Here is a comparison between no foam and case foam, using the soft dumbbell mount.</p>
                <AudioPlayer src="/audio/dk1_tkl/dk1_tkl_mx2a_softdumbbell.mp3" className="w-full mt-5" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>No Foam</p>
                <AudioPlayer src="/audio/dk1_tkl/dk1_tkl_mx2a_softdumbbell_cfoam.mp3" className="w-full mt-2" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>Case Foam</p>
              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row-reverse items-center gap-8">
              <img
                src="/pictures/articles/dk1_tkl/pic5.JPG"
                alt="Pavlov Case"
                className="w-full md:w-[55vw] h-[80vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:mr-[2vw] md:ml-[1vw] md:text-left md:max-w-[45vw]">
                <h2 className="text-3xl font-bold mb-4">Value & Competition</h2>
                <p>
                Pricing is one of the DK1-TKL’s strongest points.
                <br /><br />
                <b>Base configuration:</b> Starts at $191 (brass bottom, aluminum weight, solder PCB).
                <br /><br />
                <b>Max configuration:</b> Maxes out at $263 (copper bottom, mirror weight, tri-mode hotswap PCB).
                <br /><br />
                This range <b>undercuts</b> many competitors, such as the Neo80 Cu, by <b>$50–100</b> depending on configuration. It also competes directly with newer TKL releases like the +84 and Tofu TKL, often coming in at <b>equal or better value</b>. Adding in Unikeys’ 7% discount (use code: <b>aeykbds</b>) makes the deal even stronger.
                </p><br />
                <h2 className="text-3xl font-bold mb-4">Conclusion</h2>
                <p>
                The DK1-TKL succeeds in combining flexibility, aesthetics, and affordability into a single package. Its six mounting options allow for a genuinely wide spectrum of sound and feel, while the design choices help it stand out visually.
                <br /><br />
                For enthusiasts who want a board that can adapt to multiple preferences without breaking the bank, the DK1-TKL is easy to recommend. With extras expected at Unikeys in October, this is a board worth keeping on your radar.
                </p>
              </div>
            </div>


          </div>
          {/* Video section */}
        <div className="w-full flex flex-col items-center justify-center mb-24">
          <h2 className="text-3xl font-bold text-white mb-4 text-center">Review & Sound Test</h2>
          <div className="w-full flex justify-center">
            <iframe
              width="900"
              height="506"
              src="https://www.youtube.com/embed/qElUnVreIs4"
              title="Pavlov Build and Sound Test"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded-lg shadow-lg mx-auto w-full max-w-4xl"
            ></iframe>
          </div>
        </div>
        {/* Links section */}
        <div className="w-full flex flex-col items-center mb-24">
          <ExploreMoreLinks/>
        </div>
      </div>
    </ArticleWrapper>
  );
};

export default DK1_TKL;


